import React from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import API from '../services/api';
import { useApi } from '../hooks/useApi';

const CustomTourCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-amber-400 dark:border-amber-500 relative">
    <div className="absolute top-4 left-4 z-10 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow">
      Custom
    </div>

    <div className="relative overflow-hidden">
      <img
        src="/pics/high-res/Beach and ATV.JPG"
        alt="Special Custom Tour"
        className="w-full h-64 object-cover"
        onError={(e) => { e.target.src = '/pics/default-tour.jpg'; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
        Up to 6 hours
      </div>
    </div>

    <div className="p-6">
      <div className="mb-4">
        <h3 className="font-playfair text-xl font-bold text-gray-900 dark:text-white mb-1">
          Special Custom Tour
        </h3>
        <p className="text-amber-600 dark:text-amber-400 text-sm font-semibold">
          CONTACT FOR PRICE — Combine Rainforest, Pirates of the Caribbean and additional locations
        </p>
      </div>

      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
        This tour is completely customizable, allowing you to choose the highlights that matter
        most to you. Price is predetermined and agreed before the tour but never less than the
        listed tours. Contact us direct for price.
      </p>

      <div className="mb-4">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Includes:</h4>
        <div className="grid grid-cols-2 gap-1">
          {['Helmet / Goggles', 'Photo stops', 'Film location access', 'Movie insights'].map((item) => (
            <div key={item} className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              <span className="text-xs text-gray-600 dark:text-gray-400">{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {['Film locations', 'Couples', 'Movie fans', 'Custom'].map((tag) => (
          <span
            key={tag}
            className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-md text-xs font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Price</span>
            <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              Contact Us
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Custom quote — tailored to your group
          </p>
        </div>
        <Link
          to="/contact"
          state={{ subject: 'custom-tour' }}
          className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm border border-black/10 shadow"
        >
          Contact Us
        </Link>
      </div>
    </div>
  </div>
);

function ToursPage() {
  const { data: toursData, loading: toursLoading } = useApi(() => API.tours.getAll());
  const tours = toursData || [];
  const { data: pageContent } = useApi(() => API.content.getPageContent('tours'));


  const features = [
    {
      icon: "📍",
      title: "Pickup Locations",
      description: "New pickup points from the two (2) designated locations",
      details: [
        "Sandals ",
        "Kingstown Port",
        "Other hotels: Transportation requests must be made in advance and WILL incur additional fees.",
        "Free pickup from the two designated locations above only",
        "Punctual and reliable service"
      ]
    },
    {
      icon: "✅",
      title: "What's Included",
      description: "Everything you need for your adventure",
      details: [
        "Round-trip transportation service",
        "2-hour guided ATV tour experience",
        "Scenic waterfall visit and photo stop",
        "Typical local snack tasting",
        "Professional safety equipment & expert guides"
      ]
    },
    {
      icon: "🎒",
      title: "What to Bring",
      description: "Essential items for your comfort",
      details: [
        "Comfortable clothing & closed-toe shoes",
        "Hat, sunscreen & insect repellent",
        "Camera phone for capturing memories",
        "Backpack or drawstring bag",
        "Water bottle & light rain gear"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden page-top-spacing">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${pageContent?.hero_background?.image_url || "https://images.pexels.com/photos/1054218/pexels-photo-1054218.jpeg"}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/40"></div>
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Abstract geometric pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0 bg-repeat mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M50 50L25 25h50L50 50zM50 50l25 25H25l25-25z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>
        </div>

        <div className="relative z-10 text-center max-w-5xl mx-auto px-6">
          <div className="section-header">
            <div className="section-overline text-gold mb-4 font-semibold tracking-wide text-sm uppercase">
              Explore St. Vincent
            </div>
            <h1 className="section-title font-playfair text-5xl md:text-7xl font-bold mb-6 text-white">
              {pageContent?.hero_title?.content_value || "Our ATV Tours"}
            </h1>
          </div>
          <p className="font-graphik text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            {pageContent?.hero_description?.content_value || "Experience the thrill of St. Vincent's most exciting ATV tours"}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-6 py-3 rounded-full">
              <span className="text-emerald-300 font-semibold">Operating Hours:</span> Monday - Saturday
            </div>
            <div className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-6 py-3 rounded-full">
              <span className="text-emerald-300 font-semibold">Pricing:</span> All tours in USD$
            </div>
          </div>
          <a
            href="#tours-grid"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-graphik font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl border border-black/20"
          >
            <span>Choose Your Tour</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </a>
        </div>
      </section>

      {/* Tours Section */}
      <section id="tours-grid" className="py-20">
        <div className="container mx-auto px-6 relative">
          <div className="section-header text-center mb-16">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Choose Your Adventure
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Featured ATV Tours
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Discover the beauty of St. Vincent with our carefully crafted tour experiences
            </p>

            {/* Decorative geometric patterns */}
            <div className="absolute inset-0 -z-10 opacity-10 dark:opacity-5">
              <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-xl"></div>
              <div className="absolute bottom-10 right-10 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-xl"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {toursLoading ? (
              <div className="col-span-3 text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : tours && tours.length > 0 ? (
              <>
                {tours.map(tour => (
                <div
                  key={tour.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-200 dark:border-gray-700"
                >
                  <div className="relative overflow-hidden">
                    <ResponsiveImage
                      src={tour.image_url || '/pics/default-tour.jpg'}
                      alt={tour.name}
                      className="w-full h-64 object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      priority={true}
                    />
                    <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {tour.duration}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="font-playfair text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {tour.name}
                      </h3>
                      <p className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
                        {tour.subtitle}
                      </p>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                      {tour.description}
                    </p>

                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Includes:</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {tour.includes.map((item, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {tour.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-md text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">From</span>
                            <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                              ${tour.price_single_atv || tour.price}
                            </span>
                          </div>
                          {/* <p className="text-xs text-gray-500 dark:text-gray-400">per Single ATV</p> */}
                        </div>
                        <Link
                          to={`/tours/${tour.slug}`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm border border-black/20"
                        >
                          Book Now
                        </Link>
                      </div>

                      {/* ATV Type Pricing */}
                      <div className="flex gap-4 text-xs">
                        <div className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                          <span className="text-blue-600 dark:text-blue-400"> Single:</span>
                          <span className="font-bold text-blue-700 dark:text-blue-300">
                            ${tour.price_single_atv || tour.price}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
                          <span className="text-green-600 dark:text-green-400">Shared:</span>
                          <span className="font-bold text-green-700 dark:text-green-300">
                            ${tour.price_double_atv || (tour.price * 1.5).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            ))}
                <CustomTourCard />
              </>
            ) : (
              <div className="col-span-3 text-center py-20">
                <p className="text-gray-600 dark:text-gray-400">No tours available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-6 relative">
          <div className="section-header text-center mb-16">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Tour Information
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Everything You Need to Know
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive information to help you prepare for your adventure
            </p>

            {/* Decorative geometric patterns */}
            <div className="absolute inset-0 -z-10 opacity-10 dark:opacity-5">
              <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-xl"></div>
              <div className="absolute bottom-10 right-10 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-xl"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="font-playfair text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
                <div className="space-y-3">
                  {feature.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        {detail}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Special Notice */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-2xl p-8 border border-emerald-200 dark:border-emerald-700">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="font-playfair text-xl font-bold text-emerald-800 dark:text-emerald-200 mb-2">
                    Important Information
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-emerald-700 dark:text-emerald-300 mb-2">
                        <strong>Operating Schedule:</strong> Monday through Saturday
                      </p>
                      <p>
                        <strong>Passengers:</strong> A tandem passenger must be competent enough to hold the side grips and/or to the driver.
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-300 mb-2">
                        <strong>Age Requirement:</strong> Minimum 16 years old with a valid driver's license from your country of origin
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-300">
                        <strong>Weather Policy:</strong> Tours operate rain or shine
                      </p>
                    </div>
                    <div>
                      <p className="text-emerald-700 dark:text-emerald-300 mb-2">
                        <strong>Group Size:</strong> Maximum 8 ATVs per tour
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-300 mb-2">
                        <strong>Cancellation:</strong> 24-hour notice required
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-300">
                        <strong>Payment:</strong> All prices in USD$
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Ready for Your Adventure?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Join hundreds of satisfied customers who have experienced the thrill of St. Vincent's most exciting ATV tours. Book now and create memories that will last a lifetime.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href="#tours-grid"
                className="inline-flex items-center space-x-2 bg-white text-emerald-700 hover:bg-gray-100 font-graphik font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                <span>Choose Your Adventure</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>

              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-emerald-700 font-graphik font-semibold px-8 py-4 rounded-full transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className='text-dark'>Have Questions?</span>
              </Link>

              <Link
                to="/"
                className="inline-flex items-center space-x-2 text-white/80 hover:text-white font-graphik font-medium px-4 py-2 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Home</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ToursPage;
